import {
  Badge,
  Button,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import { Check } from 'lucide-react';
import * as React from 'react';

import { RecordSection } from '@/design';
import { secAdvApi } from '../api/sec-adv-api';
import type {
  IAdminSecAdvFiling,
  IAdminSecAdvFirmDetail,
  IAdminSecAdvFirmParticipant,
  IAdminSecAdvPrefillFact,
  ISecAdvAddress,
  ISecAdvCatalogue,
  ISecAdvCatalogueAttribute,
  ISecAdvClientType,
  ISecAdvCustody,
  ISecAdvFirmSummary
} from '../api/sec-adv-contracts';
import { instantOrAbsent } from '../components/account-facts';
import {
  SectionLevel,
  Fact,
  LoadError,
  RegisterStatusLink,
  Section,
  TableSkeleton,
  useAdminData
} from '../components/admin-page';
import { formatDay } from '../components/days';
import { ParticipantLink } from '../components/participant-link';
import { RegistryFact } from '../components/registry-fact';
import {
  CatalogueValue,
  FIRM_TYPE,
  LabelledCode,
  NotAnswered,
  codeValues,
  formatCount,
  formatDollars,
  isObject,
  matchedByText,
  useSecAdvCatalogue,
  yesNo
} from '../components/sec-adv-values';
import { TableFrame } from '../components/table-frame';
import { UnverifiedBadge } from '../components/unverified-badge';
import { HUB_MEMORY_PAGE } from './person-hub/hub-memory';

/**
 * A register firm's cards outside its own page (SR-214's card, SR-423's tabs): the register's entry
 * on a firm's record page (SR-391), and the participants and prefill preview the firm page's
 * Internal tab draws. Each renders what survey-api computed — the summary, the fact router's
 * preview — and formats it; unanswered is said, never drawn as `0` or blank.
 */

/**
 * One register firm's row, Part 1A summary and custodians, drawn on the firm's record page (SR-391)
 * under `registry.browse` — the card's own read and its own three cards, so the two screens draw a
 * firm one way. The participants, the prefill preview and the filing stay the card's: the record
 * page is the firm's, and the card is one press away.
 */
export function SecAdvRegisterEntry({ firmCrd }: { firmCrd: string }) {
  const { data, error } = useAdminData<{ firm: Nullable<IAdminSecAdvFirmDetail> }>(
    async (signal) => ({ firm: await secAdvApi.getFirm(firmCrd, signal) }),
    [firmCrd]
  );
  const labels = useSecAdvCatalogue();
  const firm = data?.firm ?? null;
  if (data === null) return error ? <LoadError message={error} /> : <TableSkeleton rows={3} />;
  if (firm === null) {
    return (
      <p className="text-sm text-muted-foreground">
        No firm in the register has CRD {firmCrd} any more.
      </p>
    );
  }
  const { summary } = firm;
  return (
    <SectionLevel.Provider value={4}>
      <div className="grid gap-4">
        {/* What a reader of the record wants first, then the register's whole card folded
            (SR-407): Part 1A drawn open was most of a firm's page. */}
        <div role="group" aria-label="Key facts" className="grid gap-3 text-sm sm:grid-cols-3">
          <CardFact label="Type">
            <LabelledCode code={firm.firmType} values={codeValues(labels.data, FIRM_TYPE)} />
          </CardFact>
          <CardFact label="Registration status">
            {answered(summary.registrationStatus, (status) => (
              <LabelledCode
                code={status}
                values={codeValues(labels.data, { path: 'Rgstn', name: 'St' })}
              />
            ))}
          </CardFact>
          <CardFact label="Latest filing">{answered(firm.filingDate, formatDay)}</CardFact>
          <CardFact label="RAUM" item="5.F(2)(c)">
            {answered(summary.raum?.total ?? null, formatDollars)}
          </CardFact>
          <CardFact label="Accounts" item="5.F(2)(f)">
            {answered(summary.raum?.totalAccounts ?? null, formatCount)}
          </CardFact>
          <CardFact label="Employees" item="5.A">
            {answered(summary.employees, formatCount)}
          </CardFact>
        </div>
        <RecordSection
          page={HUB_MEMORY_PAGE}
          sectionKey="sec-adv.full"
          title="The register’s full entry — firm, Form ADV summary, custodians"
          level={3}
          defaultOpen={false}
          filled={0}
          total={0}
        >
          <div className="grid gap-4">
            <FirmFacts firm={firm} catalogue={labels.data} />
            <SummaryCard summary={summary} catalogue={labels.data} />
            <CustodiansCard filing={firm.custodians} catalogue={labels.data} />
          </div>
        </RecordSection>
      </div>
    </SectionLevel.Provider>
  );
}

/**
 * One labelled value on the card, named by its label — so a reader moving by groups, and a spec,
 * finds "Total" under 5.F rather than a position on the page. The Form ADV item it answers sits
 * beside the label, outside the name: `Fact`'s shape, with a reference.
 */
function CardFact({
  label,
  item,
  children
}: {
  label: string;
  item?: string;
  children: React.ReactNode;
}) {
  const id = React.useId();
  return (
    <div role="group" aria-labelledby={id} className="grid content-start gap-0.5">
      <span className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
        <span id={id} className="tracking-wide uppercase">
          {label}
        </span>
        {item && <span className="font-mono">{item}</span>}
      </span>
      <div className="wrap-anywhere">{children}</div>
    </div>
  );
}

/** A value — shown as itself, or through `show` — or "Not answered" where survey-api sent null. */
function answered<T>(
  value: Nullable<T>,
  show: (value: T) => React.ReactNode = (shown) => shown as React.ReactNode
): React.ReactNode {
  return value === null ? <NotAnswered /> : show(value);
}

/** A Part 1A attribute by its name alone — a disclosure is sent as `Q11D2`, with no element.
 *  Part 1A's names are its questions' numbers, so none is spelled twice across its items. */
function part1AAttribute(
  catalogue: Nullable<ISecAdvCatalogue>,
  name: string
): Nullable<ISecAdvCatalogueAttribute> {
  for (const element of catalogue?.elements ?? []) {
    if (!element.path.startsWith('FormInfo.Part1A.')) continue;
    const found = element.attributes.find((attribute) => attribute.name === name);
    if (found) return found;
  }
  return null;
}

// ---------------------------------------------------------------------------
// The firm
// ---------------------------------------------------------------------------

/** The register's row: the seven columns of `sec_adv_firm`, as the daily feed wrote them. */
function FirmFacts({
  firm,
  catalogue
}: {
  firm: IAdminSecAdvFirmDetail;
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  return (
    <Section title="Firm" description="The register's row, from the SEC's daily feed.">
      <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
        <CardFact label="Legal name">{firm.legalName}</CardFact>
        <CardFact label="Business name">{answered(firm.businessName)}</CardFact>
        <CardFact label="SEC number">
          <span className="font-mono">{firm.secNumber}</span>
        </CardFact>
        <CardFact label="Firm CRD">
          <span className="font-mono">{firm.firmCrd}</span>
        </CardFact>
        <CardFact label="Type">
          <LabelledCode code={firm.firmType} values={codeValues(catalogue, FIRM_TYPE)} />
        </CardFact>
        <CardFact label="Latest filing">{answered(firm.filingDate, formatDay)}</CardFact>
        <CardFact label="Feed date">
          {formatDay(firm.feedDate)}
          <span className="mt-1 block text-muted-foreground">
            The daily feed this row was imported from.
          </span>
        </CardFact>
      </CardContent>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Its participants
// ---------------------------------------------------------------------------

/**
 * Who holds the firm's SEC number (as `SEC_NUMBER`) or its CRD (as `ORG_CRD`) — survey-api's
 * match, each participant once — and whether a number that matched is verified. A match is not a
 * verification, and the copy says so: verifying a number is a person's press on the
 * participant's own hub, and `unverified` is the participant's marker over every number it
 * holds, beside this.
 */
export function ParticipantsCard({
  participants
}: {
  participants: IAdminSecAdvFirmParticipant[];
}) {
  return (
    <Section title="Participants">
      <CardContent className="grid gap-3">
        {participants.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No participant holds the firm’s SEC number or its firm CRD.
          </p>
        ) : (
          <ul aria-label="Participants holding its numbers" className="grid gap-3">
            {participants.map((participant) => (
              <li
                key={participant.participantId}
                className="grid gap-3 rounded-md border border-border p-4 text-sm sm:grid-cols-2"
              >
                <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
                  <ParticipantLink participant={participant} />
                  <Badge variant="soft">{participant.kind}</Badge>
                  {participant.unverified && <UnverifiedBadge />}
                </div>
                <CardFact label="Matched by">{matchedByText(participant.matchedBy)}</CardFact>
                <CardFact label="Matched number">
                  {participant.verified ? 'Verified' : 'Not verified'}
                </CardFact>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// The summary
// ---------------------------------------------------------------------------

/** One block of the summary — a group named by its heading, so each is found by what it is. */
function SummaryBlock({
  title,
  item,
  children
}: {
  title: string;
  item?: string;
  children: React.ReactNode;
}) {
  const id = React.useId();
  return (
    <div role="group" aria-labelledby={id} className="grid content-start gap-3">
      <h3 className="flex flex-wrap items-baseline gap-x-2 text-sm font-medium">
        <span id={id} className="text-[var(--color-royal-blue)]">
          {title}
        </span>
        {item && <span className="font-mono text-xs text-muted-foreground">{item}</span>}
      </h3>
      {children}
    </div>
  );
}

/** A block's facts, two to a row. */
function FactGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2">{children}</div>;
}

/**
 * The parts of Part 1A staff read first — survey-api's typed summary of the record, blocked by
 * Form ADV item. Everything else the firm filed is in the record, beside it.
 */
function SummaryCard({
  summary,
  catalogue
}: {
  summary: ISecAdvFirmSummary;
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  const code = (value: string, path: string, name: string) => (
    <LabelledCode code={value} values={codeValues(catalogue, { path, name })} />
  );

  return (
    <Section
      title="Summary"
      description={
        'The parts of the firm’s Form ADV Part 1A read first, as the system reads them from ' +
        'its filing. Each names the item it answers.'
      }
    >
      <CardContent className="grid gap-6 text-sm">
        <SummaryBlock title="Registration">
          <FactGrid>
            <CardFact label="Registration status">
              {answered(summary.registrationStatus, (status) => code(status, 'Rgstn', 'St'))}
            </CardFact>
            <CardFact label="Status since">
              {answered(summary.registrationDate, formatDay)}
            </CardFact>
            <CardFact label="SEC regional office">
              {answered(summary.secRegion, (region) => code(region, 'Info', 'SECRgnCD'))}
            </CardFact>
            <CardFact label="Umbrella registration" item="1.B(2)">
              {answered(summary.umbrella, yesNo)}
            </CardFact>
            <CardFact label="Form ADV version">{answered(summary.formVersion)}</CardFact>
          </FactGrid>
        </SummaryBlock>

        <SummaryBlock title="Notice filings" item="2.C">
          {summary.noticeFilings.length === 0 ? (
            <p>None — the firm notice-files with no state.</p>
          ) : (
            <TableFrame>
              <Table aria-label="States it notice-files with">
                <TableHeader>
                  <TableRow>
                    <TableHead>State</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Since</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.noticeFilings.map((filing, index) => (
                    <TableRow key={`${filing.state ?? ''} ${index}`}>
                      <TableCell>{answered(filing.state, (state) => state)}</TableCell>
                      <TableCell>
                        {answered(filing.status, (status) =>
                          code(status, 'NoticeFiled.States', 'St')
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {answered(filing.date, formatDay)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableFrame>
          )}
        </SummaryBlock>

        <SummaryBlock title="Offices">
          <FactGrid>
            <CardFact label="Principal office" item="1.F">
              {answered(summary.mainAddress, (address) => (
                <AddressLines address={address} />
              ))}
            </CardFact>
            <CardFact label="Mailing address" item="1.G">
              {answered(summary.mailingAddress, (address) => (
                <AddressLines address={address} />
              ))}
            </CardFact>
          </FactGrid>
        </SummaryBlock>

        <SummaryBlock title="Websites" item="1.I">
          <Websites websites={summary.websites} />
        </SummaryBlock>

        <SummaryBlock title="Organization" item="3.A–3.C">
          {answered(summary.organization, (organization) => (
            <FactGrid>
              <CardFact label="Form of organization" item="3.A">
                {answered(organization.form, (form) =>
                  code(form, 'FormInfo.Part1A.Item3A', 'OrgFormNm')
                )}
                {organization.formOther !== null && (
                  <span className="mt-1 block">Specified: {organization.formOther}</span>
                )}
              </CardFact>
              <CardFact label="Fiscal year ends" item="3.B">
                {answered(organization.fiscalYearEnd, (month) =>
                  code(month, 'FormInfo.Part1A.Item3B', 'Q3B')
                )}
              </CardFact>
              <CardFact label="Organized under the laws of" item="3.C">
                {organization.state === null && organization.country === null ? (
                  <NotAnswered />
                ) : (
                  [organization.state, organization.country]
                    .filter((part): part is string => part !== null)
                    .join(', ')
                )}
              </CardFact>
            </FactGrid>
          ))}
        </SummaryBlock>

        <SummaryBlock title="Employees and clients">
          <CardFact label="Employees" item="5.A">
            {answered(summary.employees, formatCount)}
          </CardFact>
          <CardFact label="Clients by type" item="5.D">
            {answered(summary.clientsByType, (rows) => (
              <ClientsByType rows={rows} catalogue={catalogue} />
            ))}
          </CardFact>
        </SummaryBlock>

        <SummaryBlock title="Assets under management" item="5.F">
          {answered(summary.raum, (raum) => (
            <FactGrid>
              <CardFact label="Manages portfolios continuously and regularly" item="5.F(1)">
                {answered(raum.managesPortfolios, yesNo)}
              </CardFact>
              <CardFact label="Discretionary" item="5.F(2)(a)">
                {answered(raum.discretionary, formatDollars)}
              </CardFact>
              <CardFact label="Non-discretionary" item="5.F(2)(b)">
                {answered(raum.nonDiscretionary, formatDollars)}
              </CardFact>
              <CardFact label="Total" item="5.F(2)(c)">
                {answered(raum.total, formatDollars)}
              </CardFact>
              <CardFact label="Discretionary accounts" item="5.F(2)(d)">
                {answered(raum.discretionaryAccounts, formatCount)}
              </CardFact>
              <CardFact label="Non-discretionary accounts" item="5.F(2)(e)">
                {answered(raum.nonDiscretionaryAccounts, formatCount)}
              </CardFact>
              <CardFact label="Total accounts" item="5.F(2)(f)">
                {answered(raum.totalAccounts, formatCount)}
              </CardFact>
              <CardFact label="Of non-US clients" item="5.F(3)">
                {answered(raum.nonUsClients, formatDollars)}
              </CardFact>
            </FactGrid>
          ))}
        </SummaryBlock>

        <SummaryBlock title="Affiliations and private funds">
          <FactGrid>
            <CardFact label="Related persons" item="7.A">
              <RelatedCompanies codes={summary.relatedCompanies} />
            </CardFact>
            <CardFact label="Advises a private fund" item="7.B">
              {answered(summary.privateFunds, yesNo)}
            </CardFact>
          </FactGrid>
        </SummaryBlock>

        <SummaryBlock title="Custody the firm has" item="9.A">
          <CustodyFacts custody={summary.custody} item="9.A" />
        </SummaryBlock>

        <SummaryBlock title="Custody its related persons have" item="9.B">
          <CustodyFacts custody={summary.relatedPersonCustody} item="9.B" />
        </SummaryBlock>

        <SummaryBlock title="Disclosures" item="11">
          <Disclosures names={summary.disclosures} catalogue={catalogue} />
        </SummaryBlock>
      </CardContent>
    </Section>
  );
}

/** An address as it would be written on an envelope, from the parts the record answers, then its
 *  telephone and fax. survey-api sends no address at all where the element is empty. */
function AddressLines({ address }: { address: ISecAdvAddress }) {
  const locality = [
    address.city,
    [address.state, address.postalCode].filter((part) => part !== null).join(' ')
  ]
    .filter((part): part is string => part !== null && part !== '')
    .join(', ');
  const lines = [address.street1, address.street2, locality, address.country].filter(
    (line): line is string => line !== null && line !== ''
  );
  const contact = [
    address.phone !== null && `Telephone ${address.phone}`,
    address.fax !== null && `Fax ${address.fax}`
  ].filter((line): line is string => line !== false);

  if (lines.length === 0 && contact.length === 0) return <NotAnswered />;
  return (
    <span className="grid">
      {[...lines, ...contact].map((line, index) => (
        <span key={index}>{line}</span>
      ))}
    </span>
  );
}

/** How many websites are shown before "Show all" — a firm can file thousands (one files 3 379),
 *  and every one is kept a press away rather than dropped. */
const WEBSITES_SHOWN = 10;

/** 1.I, every one survey-api sends, as text: an address from a filing is shown, not followed. */
function Websites({ websites }: { websites: string[] }) {
  const [all, setAll] = React.useState(false);
  if (websites.length === 0) return <p>None — the firm files no website.</p>;
  const shown = all ? websites : websites.slice(0, WEBSITES_SHOWN);
  return (
    <div className="grid gap-2">
      <ul aria-label="Websites it files" className="grid gap-1">
        {shown.map((site, index) => (
          <li key={index} className="break-all">
            {site}
          </li>
        ))}
      </ul>
      {websites.length > WEBSITES_SHOWN && (
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => setAll(!all)}>
            {all ? `Show the first ${WEBSITES_SHOWN}` : `Show all ${websites.length}`}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The name of a 5.D client type, from the catalogue — its rows' columns are labelled
 * "<client type>: number of clients", so the part before the colon is the type. The whole label
 * where it has no colon, and nothing where the catalogue does not list the row: the reference
 * `5.D(a)` beside it still says which row it is.
 */
function clientTypeName(catalogue: Nullable<ISecAdvCatalogue>, type: string): Nullable<string> {
  const label = part1AAttribute(catalogue, `Q5D${type.toUpperCase()}1`)?.label;
  if (label === undefined) return null;
  const colon = label.indexOf(': ');
  return colon === -1 ? label : label.slice(0, colon);
}

/** 5.D, a row per client type the firm answered any column of. 5.D(2) is a tick or nothing: the
 *  feed writes the checkbox only when it is ticked. */
function ClientsByType({
  rows,
  catalogue
}: {
  rows: ISecAdvClientType[];
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  if (rows.length === 0) return <p>No client type answered.</p>;
  return (
    <TableFrame className="mt-1">
      <Table aria-label="Clients by type">
        <TableHeader>
          <TableRow>
            <TableHead>Client type</TableHead>
            <TableHead className="whitespace-nowrap">
              Clients <span className="font-mono text-xs font-normal">5.D(1)</span>
            </TableHead>
            <TableHead className="whitespace-nowrap">
              Fewer than 5 <span className="font-mono text-xs font-normal">5.D(2)</span>
            </TableHead>
            <TableHead className="whitespace-nowrap">
              RAUM <span className="font-mono text-xs font-normal">5.D(3)</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.type}>
              <TableCell className="min-w-48">
                <span className="mr-2 font-mono text-xs text-muted-foreground">
                  5.D({row.type})
                </span>
                {clientTypeName(catalogue, row.type)}
                {row.description !== null && (
                  <span className="mt-0.5 block text-muted-foreground">{row.description}</span>
                )}
              </TableCell>
              <TableCell>{answered(row.count, formatCount)}</TableCell>
              <TableCell>
                {row.fewerThanFive && (
                  <>
                    <Check className="size-4" aria-hidden />
                    <span className="sr-only">Fewer than 5 clients</span>
                  </>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {answered(row.raum, formatDollars)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableFrame>
  );
}

/** 7.A as survey-api carries it for a response's prefill — `ria_related_companies` option codes,
 *  a survey's vocabulary rather than Form ADV's, so shown as sent: labelling them would take the
 *  survey definitions, which this card does not read. */
function RelatedCompanies({ codes }: { codes: Nullable<string[]> }) {
  if (codes === null) return <NotAnswered />;
  if (codes.length === 0) return 'None';
  return (
    <>
      <ul aria-label="Related person codes" className="grid gap-0.5">
        {codes.map((code) => (
          <li key={code}>
            <code className="text-xs">{code}</code>
          </li>
        ))}
      </ul>
      <span className="mt-1 block text-muted-foreground">
        The survey’s option codes, as a response is prefilled with them. 7.A is labelled in full
        in the filing below.
      </span>
    </>
  );
}

/** 9.A or 9.B — the same four questions, of the firm or of its related persons. */
function CustodyFacts({ custody, item }: { custody: Nullable<ISecAdvCustody>; item: string }) {
  if (custody === null) return <NotAnswered />;
  return (
    <FactGrid>
      <CardFact label="Cash or bank accounts" item={`${item}(1)(a)`}>
        {answered(custody.cash, yesNo)}
      </CardFact>
      <CardFact label="Securities" item={`${item}(1)(b)`}>
        {answered(custody.securities, yesNo)}
      </CardFact>
      <CardFact label="Amount held" item={`${item}(2)(a)`}>
        {answered(custody.amount, formatDollars)}
      </CardFact>
      <CardFact label="Clients" item={`${item}(2)(b)`}>
        {answered(custody.clients, formatCount)}
      </CardFact>
    </FactGrid>
  );
}

/** Item 11's questions answered Yes, each by the catalogue's label for it and its reference,
 *  with the question's own name beside — the name alone where the catalogue has no label. */
function Disclosures({
  names,
  catalogue
}: {
  names: string[];
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  if (names.length === 0) return <p>None — no Item 11 question is answered Yes.</p>;
  return (
    <ul aria-label="Questions answered Yes" className="grid gap-2">
      {names.map((name) => {
        const question = part1AAttribute(catalogue, name);
        return (
          <li key={name} className="flex flex-wrap items-baseline gap-x-2">
            {question?.item && <span className="font-mono text-xs">{question.item}</span>}
            {question && <span>{question.label}</span>}
            <code className={question ? 'text-xs text-muted-foreground' : undefined}>{name}</code>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Its custodians
// ---------------------------------------------------------------------------

/**
 * The custodians of the firm's latest filing the monthly import read — Schedule D 5.K.(3), as
 * survey-api stores them — with the window said as the rule it is: a filing older than the
 * window stays held and is shown, and it is the one a lookup answers `not_found` for. The card
 * shows what the register holds, not what a lookup would use.
 *
 * `submittedAt` is shown as the archive writes it, with no time zone and no conversion: read as an
 * instant it would be converted from a zone nobody stated. The table's columns are the
 * catalogue's custodian members in its order, then any member a custodian carries that the
 * catalogue does not list, by its name — nothing a filing holds is dropped.
 */
function CustodiansCard({
  filing,
  catalogue
}: {
  filing: Nullable<IAdminSecAdvFiling>;
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  return (
    <Section
      title="Custodians"
      description={
        'The custodians the firm’s latest filing read by the monthly import names (Schedule D ' +
        '5.K(3)) — what the register holds, whether or not a lookup would use it.'
      }
    >
      <CardContent className="grid gap-4 text-sm">
        {filing === null ? (
          <p className="text-muted-foreground">
            No filing of this firm has been read by the monthly import.
          </p>
        ) : (
          <>
            <FactGrid>
              <CardFact label="Filing id">
                <span className="font-mono">{filing.filingId}</span>
              </CardFact>
              <CardFact label="Submitted">
                <span className="font-mono">{filing.submittedAt}</span>
                <span className="mt-1 block text-muted-foreground">
                  As the archive writes it, with no time zone.
                </span>
              </CardFact>
              <div className="sm:col-span-2">
                <CardFact label="Filing window">
                  {filing.inWindow ? (
                    <>
                      Inside the window a lookup of <code className="text-xs">adv.custodians</code>{' '}
                      reads from.
                    </>
                  ) : (
                    <>
                      Older than the window a lookup reads from:{' '}
                      <code className="text-xs">adv.custodians</code> answers{' '}
                      <code className="text-xs">not_found</code> for this firm. It is shown because
                      the register still holds it.
                    </>
                  )}{' '}
                  The window’s months are under{' '}
                  <RegisterStatusLink />
                  .
                </CardFact>
              </div>
            </FactGrid>
            <CustodianTable custodians={filing.custodians} catalogue={catalogue} />
          </>
        )}
      </CardContent>
    </Section>
  );
}

function CustodianTable({
  custodians,
  catalogue
}: {
  custodians: unknown[];
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  if (custodians.length === 0) return <p>The filing names no custodian.</p>;

  const listed = catalogue?.custodians ?? [];
  const unlisted: string[] = [];
  for (const custodian of custodians) {
    // A custodian is `unknown` on the wire (roadmap D8): only an object has members to list.
    for (const name of Object.keys(isObject(custodian) ? custodian : {})) {
      if (!listed.some((member) => member.name === name) && !unlisted.includes(name)) {
        unlisted.push(name);
      }
    }
  }
  const columns: { name: string; attribute: Nullable<ISecAdvCatalogueAttribute> }[] = [
    ...listed.map((attribute) => ({ name: attribute.name, attribute })),
    ...unlisted.map((name) => ({ name, attribute: null }))
  ];

  return (
    <TableFrame>
      <Table aria-label="Custodians the filing names">
        <TableHeader>
          <TableRow>
            {columns.map(({ name, attribute }) => (
              <TableHead key={name} className="min-w-36 align-top whitespace-normal">
                {attribute?.item && (
                  <span className="block font-mono text-xs font-normal">{attribute.item}</span>
                )}
                {attribute ? attribute.label : <code>{name}</code>}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {custodians.map((custodian, index) => (
            <TableRow key={index}>
              {isObject(custodian) ? (
                columns.map(({ name, attribute }) => (
                  <TableCell key={name} className="align-top">
                    <CatalogueValue attribute={attribute} value={custodian[name]} />
                  </TableCell>
                ))
              ) : (
                <TableCell colSpan={columns.length} className="font-mono text-xs">
                  {JSON.stringify(custodian)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableFrame>
  );
}

// ---------------------------------------------------------------------------
// The prefill preview
// ---------------------------------------------------------------------------

/**
 * What a survey response opened now would pin for this firm — survey-api asks its fact router for
 * every key a response pins, in pin order, the way a response asks — drawn as the person hub
 * draws a registry's answer (`RegistryFact`), its payload folded alike. It is a preview of an
 * opening, not a promise about a response already open: those pinned what the register answered
 * when they were opened.
 */
export function PreviewCard({ prefill }: { prefill: IAdminSecAdvPrefillFact[] }) {
  // Every `not_found` fact is one line (SR-423): six cards saying "no record" said nothing six
  // times.
  const found = prefill.filter((fact) => fact.status !== 'not_found');
  const missing = prefill.filter((fact) => fact.status === 'not_found');
  return (
    <Section title="Prefill preview">
      <CardContent className="grid gap-3">
        {prefill.length === 0 && (
          // survey-api sends the six keys a response pins; none at all is its answer to say, and
          // an empty section would read as one that failed to draw.
          <p className="text-sm text-muted-foreground">The system sent nothing to preview.</p>
        )}
        {found.length > 0 && (
          <ul aria-label="What a response opened now would pin" className="grid gap-3">
            {found.map((fact) => (
              <RegistryFact
                key={fact.factKey}
                fact={fact}
                words={previewWords(fact)}
                payloadLabel="The answer as a response would pin it"
                foldLabel="As a response would pin it"
              >
                <Fact
                  label="As of"
                  value={instantOrAbsent(fact.asOf, 'The register gave no date of its own.')}
                />
              </RegistryFact>
            ))}
          </ul>
        )}
        {missing.length > 0 && (
          <p data-prefill-none className="text-sm text-muted-foreground">
            {found.length === 0
              ? 'No prefill data for this firm'
              : `No prefill data for ${missing.map((fact) => fact.factKey).join(', ')}`}
          </p>
        )}
      </CardContent>
    </Section>
  );
}

function previewWords(fact: IAdminSecAdvPrefillFact): React.ReactNode {
  switch (fact.status) {
    case 'ok':
      return 'The register answers with a record — a response opened now would pin it, as below.';
    case 'not_found':
      return (
        'The register answers that it holds no record for this firm under this key — a ' +
        'response opened now would pin that.'
      );
    case 'error':
      return (
        <>
          The register could not answer — a response opened now would pin an error, which says
          nothing about the firm. Why is in the answer below; whether the register is fresh is
          under{' '}
          <RegisterStatusLink />
          .
        </>
      );
    default:
      // Closed in the type, open on the wire.
      return `The register answered “${fact.status as string}”, which this app has no words for.`;
  }
}
